/* eslint-disable style/quote-props */
import type { FilterConfig, FilterOption } from './Filter.types';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../styles';

interface FilterModalProps {
  // Filter configuration
  filters?: FilterConfig[];
  onFilterChange?: (filterId: string, value: string | string[]) => void;

  // Clear all filters
  onClearAllFilters?: () => void;

  // Current filter state exposed to consumers
  currentFilters?: Record<string, string | string[]>;

  // Modal visibility
  open: boolean;
  onClose: () => void;
}

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    min-width: 600px;
    max-width: 800px;
    border-radius: 12px;
  }

  @media (max-width: 768px) {
    .MuiDialog-paper {
      min-width: 90vw;
      max-width: 90vw;
      margin: 16px;
    }
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  padding: 16px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  && {
    .MuiToggleButton-root {
      position: relative;
      border-radius: 0;
      text-transform: none;
      color: #6b7280;
      border-color: #d1d5db;
      overflow: visible;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &.Mui-selected {
        background-color: var(--color-brand-primary);
        color: white;
        border-color: var(--color-darker-blue);
        
        &:hover {
          background-color: var(--color-brand-primary);
        }
      }
      
      &:hover {
        background-color: var(--color-cornflower-blue);
      }
      
      &:first-of-type {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
      }
      
      &:last-of-type {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
      }
      
      &:not(:first-of-type) {
        border-left-color: transparent;
        margin-left: -1px;
      }
    }
  }
`;

const StyledBadge = styled.div<{ count: number }>`
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 18px;
  width: 18px;
  height: 18px;
  background-color: var(--color-brand-primary);
  color: #fff;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  z-index: 10;
  border: 2px solid #ffffff;
  
  &::after {
    content: "${props => props.count}";
  }
`;

const FilterModal: React.FC<FilterModalProps> = ({
  filters = [],
  onFilterChange,
  onClearAllFilters,
  currentFilters = {},
  open,
  onClose,
}) => {
  // Local state for debounced textfields
  const [localTextfieldValues, setLocalTextfieldValues] = useState<Record<string, string>>({});

  // Initialize local textfield values when modal opens
  useEffect(() => {
    if (open) {
      const textfieldValues: Record<string, string> = {};
      filters.forEach((filter) => {
        if (filter.type === 'textfield' && !filter.multiple) {
          textfieldValues[filter.id] = (currentFilters[filter.id] as string) || '';
        }
      });
      setLocalTextfieldValues(textfieldValues);
    }
  }, [open, filters, currentFilters]);

  // Helper function to wrap components with tooltip if provided
  const withTooltip = (component: React.ReactNode, tooltip?: string) => {
    if (tooltip) {
      return (
        <Tooltip title={tooltip} arrow placement="top">
          <div style={{ display: 'inline-block', width: '100%' }}>
            {component}
          </div>
        </Tooltip>
      );
    }
    return component;
  };

  // Handle filter changes (but don't apply until modal is closed)
  const handleLocalFilterChange = (filterId: string, value: string | string[]) => {
    if (onFilterChange) {
      onFilterChange(filterId, value);
    }
  };

  // Handle textfield changes with local state
  const handleTextfieldChange = (filterId: string, value: string) => {
    setLocalTextfieldValues(prev => ({
      ...prev,
      [filterId]: value,
    }));
    // Apply immediately for textfields
    handleLocalFilterChange(filterId, value);
  };

  // Check if any clearable filters are applied (exclude exclusive toggles)
  const hasActiveClearableFilters = () => {
    return Object.entries(currentFilters).some(([filterId, value]) => {
      const filterConfig = filters.find(f => f.id === filterId);
      if (!filterConfig || (filterConfig.type === 'toggle' && filterConfig.exclusive)) {
        return false;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '' && value !== undefined && value !== null;
    });
  };

  // Check if there are any clearable filters in the configuration
  const hasClearableFilters = () => {
    return filters.some((filter) => {
      if (filter.type === 'toggle' && filter.exclusive) {
        return false;
      }
      return true;
    });
  };

  // Render individual filter based on type
  const renderFilter = (filter: FilterConfig) => {
    switch (filter.type) {
      case 'textfield': {
        if (filter.multiple) {
          // Handle multiple textfield with chips using Autocomplete
          const currentValue = (currentFilters[filter.id] as string[]) || [];

          return withTooltip(
            <FilterItem key={filter.id}>
              <Typography variant="subtitle2" fontWeight={600}>
                {filter.label}
              </Typography>
              <Autocomplete
                multiple
                freeSolo
                options={[]} // No predefined options for free text entry
                value={currentValue}
                onChange={(_event, newValue) => {
                  // Filter out empty strings and ensure uniqueness
                  const filteredValues = newValue.filter((val, index, arr) =>
                    typeof val === 'string' && val.trim() !== '' && arr.indexOf(val) === index,
                  ) as string[];
                  handleLocalFilterChange(filter.id, filteredValues);
                }}
                onInputChange={(_event, newInputValue, reason) => {
                  // Handle comma-separated input by splitting and adding multiple values
                  if (reason === 'input' && newInputValue.includes(',')) {
                    const newValues = newInputValue
                      .split(',')
                      .map(val => val.trim())
                      .filter(val => val !== '' && !currentValue.includes(val));

                    if (newValues.length > 0) {
                      const updatedValues = [...currentValue, ...newValues];
                      handleLocalFilterChange(filter.id, updatedValues);
                    }
                  }
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={currentValue.length > 0 ? 'Add another...' : filter.placeholder}
                    size={filter.size || 'small'}
                    data-testid={filter.testId}
                    InputProps={{
                      ...params.InputProps,
                      sx: { borderRadius: '12px' },
                    } as any}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      sx={{ height: 24 }}
                    />
                  ))}
                size={filter.size || 'small'}
                fullWidth
              />
            </FilterItem>,
            filter.tooltip,
          );
        }

        // Handle single textfield
        const currentValue = localTextfieldValues[filter.id] ?? '';

        return withTooltip(
          <FilterItem key={filter.id}>
            <Typography variant="subtitle2" fontWeight={600}>
              {filter.label}
            </Typography>
            <TextField
              slotProps={{ input: { sx: { borderRadius: '12px' } } }}
              placeholder={filter.placeholder}
              size={filter.size || 'small'}
              value={currentValue}
              onChange={e => handleTextfieldChange(filter.id, e.target.value)}
              data-testid={filter.testId}
              fullWidth
            />
          </FilterItem>,
          filter.tooltip,
        );
      }

      case 'select': {
        const currentValue = currentFilters[filter.id] || (filter.multiple ? [] : '');

        if (filter.autocomplete) {
          const selectedOptions = filter.multiple
            ? (currentValue as string[]).map(val => filter.options?.find(opt => opt.value === val)).filter(Boolean) as FilterOption[]
            : filter.options?.find(opt => opt.value === currentValue) || null;

          return withTooltip(
            <FilterItem key={filter.id}>
              <Typography variant="subtitle2" fontWeight={600}>
                {filter.label}
              </Typography>
              <Autocomplete<FilterOption, boolean, true, false>
                disableClearable
                options={filter.options || []}
                value={filter.multiple ? selectedOptions as FilterOption[] : (selectedOptions as FilterOption | null || undefined)}
                onChange={(_event, newValue) => {
                  if (filter.multiple && Array.isArray(newValue)) {
                    const values = newValue.map(option => option.value);
                    handleLocalFilterChange(filter.id, values);
                  }
                  else if (!filter.multiple && newValue && !Array.isArray(newValue)) {
                    handleLocalFilterChange(filter.id, newValue.value);
                  }
                }}
                getOptionLabel={(option: FilterOption) => option.name}
                isOptionEqualToValue={(option: FilterOption, value: FilterOption) => option.value === value.value}
                groupBy={(option: FilterOption) => option.group || ''}
                renderGroup={params => params.group
                  ? (
                      <li key={params.key}>
                        <Typography
                          variant="subtitle2"
                          sx={{ backgroundColor: '#f0f0f0', padding: '4px 8px', fontWeight: 600 }}
                        >
                          {params.group}
                        </Typography>
                        <ul style={{ margin: 0, padding: 0 }}>{params.children}</ul>
                      </li>
                    )
                  : <ul key={params.key} style={{ margin: 0, padding: 0 }}>{params.children}</ul>}
                renderOption={(props, option: FilterOption) => (
                  <Box component="li" {...props} key={option.value}>
                    <Typography variant="body1">
                      {option.name}
                    </Typography>
                  </Box>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.value}
                      label={option.name}
                      size="small"
                      onDelete={() => {
                        const newValues = (currentValue as string[]).filter(val => val !== option.value);
                        handleLocalFilterChange(filter.id, newValues);
                      }}
                    />
                  ))}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder={filter.placeholder}
                    size={filter.size || 'small'}
                    data-testid={filter.testId}
                    fullWidth
                    InputProps={params.InputProps as any}
                  />
                )}
                multiple={filter.multiple}
                size={filter.size || 'small'}
                fullWidth
              />
            </FilterItem>,
            filter.tooltip,
          );
        }

        // Fallback to regular Select
        return withTooltip(
          <FilterItem key={filter.id}>
            <Typography variant="subtitle2" fontWeight={600}>
              {filter.label}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id={`${filter.id}-label`} size={filter.size === 'medium' ? 'medium' : 'small'}>
                {filter.placeholder || 'Select...'}
              </InputLabel>
              <Select
                labelId={`${filter.id}-label`}
                label={filter.placeholder || 'Select...'}
                name={filter.id}
                data-testid={filter.testId}
                size={filter.size || 'small'}
                value={currentValue}
                onChange={(event) => {
                  const value = event.target.value;
                  handleLocalFilterChange(filter.id, value);
                }}
                multiple={filter.multiple}
                fullWidth
                input={filter.multiple ? <OutlinedInput label={filter.placeholder || 'Select...'} /> : undefined}
                renderValue={filter.multiple
                  ? selected => (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(selected as string[]).map((value) => {
                        const option = filter.options?.find(opt => opt.value === value);
                        return (
                          <Chip
                            key={value}
                            label={option?.name || value}
                            size="small"
                            onDelete={() => {
                              const newValues = (selected as string[]).filter(val => val !== value);
                              handleLocalFilterChange(filter.id, newValues);
                            }}
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                          />
                        );
                      })}
                    </div>
                  )
                  : undefined}
              >
                {filter.clearable && (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                )}
                {filter.options?.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {filter.multiple
                      ? (
                          <>
                            <input
                              type="checkbox"
                              checked={((currentFilters[filter.id] as string[]) || []).includes(option.value)}
                              readOnly
                              style={{ marginRight: 8 }}
                            />
                            <ListItemText primary={option.name} />
                          </>
                        )
                      : (
                          option.name
                        )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterItem>,
          filter.tooltip,
        );
      }

      case 'toggle': {
        const toggleValue = currentFilters[filter.id];
        const handleToggleChange = (
          _event: React.MouseEvent<HTMLElement>,
          newValue: string | string[] | null,
        ) => {
          if (filter.exclusive) {
            if (newValue !== null && newValue !== '') {
              handleLocalFilterChange(filter.id, newValue);
            }
          }
          else {
            handleLocalFilterChange(filter.id, newValue || []);
          }
        };

        return (
          <FilterItem key={filter.id}>
            {filter.showLabel !== false && (
              <Typography variant="subtitle2" fontWeight={600}>
                {filter.label}
              </Typography>
            )}
            <StyledToggleButtonGroup
              value={toggleValue}
              onChange={handleToggleChange}
              exclusive={filter.exclusive}
              data-testid={filter.testId}
            >
              {(filter.options || []).map((option) => {
                const buttonContent = (
                  <ToggleButton key={option.value} value={option.value}>
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      width: '100%',
                    }}
                    >
                      {option.icon && (
                        <span style={{
                          marginRight: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          lineHeight: 1,
                        }}
                        >
                          {option.icon}
                        </span>
                      )}
                      <span style={{ lineHeight: 1 }}>
                        {option.name}
                      </span>
                    </span>
                    {option.count !== undefined && option.count > 0 && (
                      <StyledBadge count={option.count} />
                    )}
                  </ToggleButton>
                );

                return option.tooltip
                  ? (
                      <Tooltip key={option.value} title={option.tooltip} arrow placement="top">
                        {buttonContent}
                      </Tooltip>
                    )
                  : buttonContent;
              })}
            </StyledToggleButtonGroup>
          </FilterItem>
        );
      }

      default:
        return null;
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            Filter Options
          </Typography>
          <IconButton onClick={onClose} size="small">
            <ClearIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <FilterGrid>
          {filters.map(renderFilter)}
        </FilterGrid>
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px' }}>
        {hasClearableFilters() && hasActiveClearableFilters() && (
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={onClearAllFilters}
            sx={{ mr: 'auto' }}
          >
            Clear All
          </Button>
        )}
        <Button onClick={onClose} variant="contained" size="large" sx={{ backgroundColor: Colors.brandPrimary, color: Colors.white, '&:hover': { backgroundColor: Colors.darkerBlue } }}>
          Done
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default FilterModal;
